import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, Zap, Heart, Waves, Sun, Moon, Play } from 'lucide-react';
import TemplatePreview from './TemplatePreview';

export interface ReelTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  animations: {
    image: string;
    video: string;
    text: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  transitionSpeed: number;
}

export const REEL_TEMPLATES: ReelTemplate[] = [
  {
    id: 'therapeutic-calm',
    name: 'Therapeutic Calm',
    description: 'Gentle, soothing transitions with healing energy',
    icon: <Waves className="w-5 h-5" />,
    animations: {
      image: 'soft-zoom-fade',
      video: 'gentle-slide',
      text: 'fade-glow'
    },
    colors: {
      primary: 'from-primary to-accent',
      secondary: 'from-muted to-accent',
      accent: 'text-ring'
    },
    transitionSpeed: 1.0
  },
  {
    id: 'golden-radiance',
    name: 'Golden Radiance',
    description: 'Empowering transitions with premium gold energy',
    icon: <Sun className="w-5 h-5" />,
    animations: {
      image: 'radial-zoom',
      video: 'bright-fade',
      text: 'glow-expand'
    },
    colors: {
      primary: 'from-secondary to-secondary',
      secondary: 'from-secondary/50 to-secondary',
      accent: 'text-secondary'
    },
    transitionSpeed: 0.7
  },
  {
    id: 'electric-flow',
    name: 'Electric Flow',
    description: 'Dynamic, energizing transitions with turquoise power',
    icon: <Zap className="w-5 h-5" />,
    animations: {
      image: 'scale-pulse',
      video: 'quick-zoom',
      text: 'bounce-glow'
    },
    colors: {
      primary: 'from-ring to-accent',
      secondary: 'from-muted to-ring',
      accent: 'text-ring'
    },
    transitionSpeed: 0.5
  },
  {
    id: 'teal-harmony',
    name: 'Teal Harmony',
    description: 'Balanced, flowing transitions with deep teal essence',
    icon: <Waves className="w-5 h-5" />,
    animations: {
      image: 'wave-slide',
      video: 'fluid-zoom',
      text: 'wave-fade'
    },
    colors: {
      primary: 'from-primary to-muted',
      secondary: 'from-accent to-muted',
      accent: 'text-muted'
    },
    transitionSpeed: 0.9
  },
  {
    id: 'luxe-mystical',
    name: 'Luxe Mystical',
    description: 'Premium, elegant transitions with gold and teal fusion',
    icon: <Sparkles className="w-5 h-5" />,
    animations: {
      image: 'zoom-rotate-fade',
      video: 'mystical-slide',
      text: 'shimmer-fade'
    },
    colors: {
      primary: 'from-secondary to-accent',
      secondary: 'from-primary to-secondary',
      accent: 'text-ring'
    },
    transitionSpeed: 0.8
  },
  {
    id: 'serene-glow',
    name: 'Serene Glow',
    description: 'Peaceful, ethereal transitions with calming light',
    icon: <Heart className="w-5 h-5" />,
    animations: {
      image: 'ethereal-fade',
      video: 'gentle-slide',
      text: 'fade-up-glow'
    },
    colors: {
      primary: 'from-muted to-ring',
      secondary: 'from-accent to-muted',
      accent: 'text-secondary'
    },
    transitionSpeed: 1.2
  }
];

interface ReelTemplateSelectorProps {
  selectedTemplate: ReelTemplate | null;
  onSelect: (template: ReelTemplate) => void;
}

export function ReelTemplateSelector({ selectedTemplate, onSelect }: ReelTemplateSelectorProps) {
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<ReelTemplate | null>(null);

  const getAnimationClass = (templateId: string) => {
    const template = REEL_TEMPLATES.find(t => t.id === templateId);
    if (!template) return '';
    
    const animationMap: Record<string, string> = {
      'zoom-rotate-fade': 'animate-[zoom-rotate-fade_3s_ease-in-out_infinite]',
      'scale-pulse': 'animate-[scale-pulse_2s_ease-in-out_infinite]',
      'soft-zoom-fade': 'animate-[soft-zoom_4s_ease-in-out_infinite]',
      'wave-slide': 'animate-[wave-slide_3s_ease-in-out_infinite]',
      'radial-zoom': 'animate-[radial-zoom_2.5s_ease-in-out_infinite]',
      'ethereal-fade': 'animate-[ethereal-fade_4s_ease-in-out_infinite]'
    };

    return animationMap[template.animations.image] || '';
  };

  return (
    <>
      {previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Choose a Template</h3>
          <p className="text-sm text-muted-foreground">
            Click the preview button to see the full template in action
          </p>
        </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {REEL_TEMPLATES.map((template) => (
          <Card
            key={template.id}
            className={`cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
              selectedTemplate?.id === template.id
                ? 'border-primary border-2 shadow-soft ring-2 ring-primary/20'
                : 'border-border hover:border-primary/50 hover:shadow-soft'
            }`}
            onClick={() => onSelect(template)}
            onMouseEnter={() => setHoveredTemplate(template.id)}
            onMouseLeave={() => setHoveredTemplate(null)}
          >
            <div className={`h-32 bg-gradient-to-br ${template.colors.primary} flex items-center justify-center relative overflow-hidden`}>
              {/* Animated preview element */}
              <div className={`absolute inset-0 transition-all duration-500 ${
                hoveredTemplate === template.id 
                  ? `bg-gradient-to-br ${template.colors.secondary} ${getAnimationClass(template.id)} opacity-80` 
                  : 'opacity-0'
              }`} />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              
              {/* Icon with animation preview */}
              <div className={`relative text-white drop-shadow-lg transition-all duration-500 z-10 ${
                hoveredTemplate === template.id ? 'scale-150 rotate-12' : 'scale-100 rotate-0'
              }`}>
                {template.icon}
              </div>
              
              {/* Animated text preview */}
              {hoveredTemplate === template.id && (
                <div className="absolute bottom-4 left-0 right-0 text-center z-10">
                  <p className={`text-white text-xs font-semibold drop-shadow-lg px-2 ${
                    template.animations.text === 'fade-up-glow' ? 'animate-[fade-up-glow_1.5s_ease-out_infinite]' :
                    template.animations.text === 'bounce-glow' ? 'animate-[bounce-glow_1.5s_ease-out_infinite]' :
                    template.animations.text === 'glow-expand' ? 'animate-[glow-expand_1.5s_ease-out_infinite]' :
                    template.animations.text === 'shimmer-fade' ? 'animate-[shimmer-fade_2s_ease-in-out_infinite]' :
                    'animate-[fade-glow_1.5s_ease-in-out_infinite]'
                  }`}>
                    Preview
                  </p>
                </div>
              )}
              
              {/* Speed indicator */}
              <div className="absolute bottom-2 left-2">
                <Badge variant="secondary" className="text-xs bg-black/50 text-white border-0">
                  {template.transitionSpeed < 0.7 ? 'Fast' : template.transitionSpeed > 1.0 ? 'Slow' : 'Medium'}
                </Badge>
              </div>
              
              {/* Selected badge */}
              {selectedTemplate?.id === template.id && (
                <div className="absolute top-2 right-2">
                  <Badge className="bg-primary text-primary-foreground">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Selected
                  </Badge>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-card">
              <h4 className="font-semibold mb-1 text-foreground">{template.name}</h4>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">{template.description}</p>
              
              {/* Preview button */}
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewTemplate(template);
                }}
              >
                <Play className="w-3 h-3 mr-1" />
                Preview Template
              </Button>
              
              {/* Animation style tags */}
              <div className="flex flex-wrap gap-1 mt-2">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {template.animations.image.replace(/-/g, ' ')}
                </Badge>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
    </>
  );
}
